import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const FILTERS = [["all", "All"], ["new", "New"], ["read", "Read"]];

const MessageCard = ({ m, onToggle }) => (
  <article className={`rounded-2xl border p-5 bg-white ${m.status === "new" ? "border-[#C96F53]/50" : "border-[#D8CDBC]"}`} data-testid={`message-${m.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-serif text-xl">{m.subject}</p>
        <p className="text-sm text-[#5E4D5A] mt-1">
          {m.name} · <a href={`mailto:${m.email}`} className="text-[#59394F] underline underline-offset-2">{m.email}</a>
          {m.order_number && <> · Order <span className="font-medium text-[#5A433B]">{m.order_number}</span></>}
        </p>
        <p className="text-xs text-[#5E4D5A] mt-1">{new Date(m.created_at).toLocaleString()}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${m.status === "new" ? "bg-[#C96F53]/15 text-[#8E4A36]" : "bg-[#EAE0D2] text-[#5A433B]"}`} data-testid={`message-status-${m.id}`}>
          {m.status === "new" ? "New" : "Read"}
        </span>
        <button onClick={() => onToggle(m)} className="text-xs rounded-full border border-[#59394F] px-3 py-1.5 hover:bg-[#59394F] hover:text-[#F4EBDD] transition-colors" data-testid={`message-toggle-${m.id}`}>
          {m.status === "new" ? "Mark as read" : "Mark as new"}
        </button>
      </div>
    </div>
    <p className="mt-4 text-sm text-[#5A433B] whitespace-pre-wrap leading-relaxed">{m.message}</p>
  </article>
);

export default function AdminMessages() {
  const { getToken } = useAuth();
  const [filter, setFilter] = useState("all");
  const [messages, setMessages] = useState(null);

  const load = useCallback(async () => {
    const headers = { Authorization: `Bearer ${await getToken()}` };
    const r = await api.get("/admin/messages", { headers, params: filter === "all" ? {} : { status: filter } });
    setMessages(r.data);
  }, [getToken, filter]);

  useEffect(() => { load().catch(() => toast.error("Could not load messages")); }, [load]);

  const toggle = async (m) => {
    const headers = { Authorization: `Bearer ${await getToken()}` };
    await api.patch(`/admin/messages/${m.id}`, { status: m.status === "new" ? "read" : "new" }, { headers });
    window.dispatchEvent(new Event("kocm:messages-changed"));
    load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="admin-messages">
      <h1 className="font-serif text-4xl">Messages</h1>
      <p className="text-sm text-[#5E4D5A] mt-2">Messages sent through the Contact page. Visible to admins only.</p>
      <div className="mt-6 mb-6 inline-flex rounded-full border border-[#D8CDBC] bg-white p-1">
        {FILTERS.map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`messages-filter-${v}`}
            className={`px-4 py-1.5 text-xs rounded-full transition-colors ${filter === v ? "bg-[#59394F] text-white" : "text-[#5A433B] hover:bg-[#A8B59A]/15"}`}>
            {label}
          </button>
        ))}
      </div>
      {messages && messages.length === 0 && <p className="text-[#5E4D5A] py-10 text-center" data-testid="messages-empty">No messages yet.</p>}
      <div className="space-y-4">
        {(messages || []).map((m) => <MessageCard key={m.id} m={m} onToggle={toggle} />)}
      </div>
    </div>
  );
}
